import { useCallback } from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import { vn } from 'vend-number'
import trim from 'lodash/trim'
import { trackError } from '@vend/utilities'
import {
  hasCashDiscountingServiceFee,
  roundCash,
} from '../../../../functions/payments'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleTotals,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { isCorrectlyRounded } from '../../../helpers/isCorrectlyRounded'
import {
  CashDiscountingPaymentOption,
  CashPaymentOption,
  IntegratedPaymentOption,
  SalePayment,
} from '../PaymentModal/types'
import { useRegisterManager, useSaleManager } from '../../../../ng-compat/hooks'
import { CashPaymentInformation } from '../../../payments/cash-payment-modal/CashPaymentModal'
import { useHandleIntegrationPayment } from './useHandleIntegrationPayment'
import { useFetchPaymentTypes } from './useFetchPaymentTypes'

/**
 * Handle complete cash payment
 * @param onPaymentSuccess
 * @param ronudingPaymentType optional rounding payment type. In rare case it will be null but most of the time it is not
 * @returns function to handle complete round cash payment.
 * This means if the payment hassome remainder, it will use the rounding payment type to pay the remainder
 * then proceed to save the cash payment to the sale via onPaymentSuccess
 * */
export const useCompleteCashPayment = (
  onPaymentSuccess: (result: SalePayment) => void,
  ronudingPaymentType?: ApiRetailerPaymentType
) => {
  const saleManager = useSaleManager()

  const tenderRoundedCash = useCallback(
    (cashAmount: string, paymentType: ApiRetailerPaymentType) => {
      onPaymentSuccess({
        paymentType,
        paymentAmount: cashAmount,
      })
    },
    [onPaymentSuccess]
  )

  const completeRoundCashPayment = useCallback(
    (
      { remainder, cashAmount }: CashPaymentInformation,
      paymentType: ApiRetailerPaymentType
    ) => {
      if (vn(0).eq(remainder)) {
        tenderRoundedCash(cashAmount, paymentType)
      } else if (ronudingPaymentType) {
        // Original code force types as not nullable - which is not safe
        return saleManager
          .pay(remainder, ronudingPaymentType)
          .catch(err => {
            trackError(err)
          })
          .finally(() => {
            tenderRoundedCash(cashAmount, paymentType)
          })
      }
    },
    [ronudingPaymentType, saleManager, tenderRoundedCash]
  )

  return {
    completeRoundCashPayment,
  }
}

/**
 * Handle cash payment
 * @returns function to handle cash payment, it should do all the necessary things to build cash payment option
 */
export const useHandleCashPayment = (
  amount: string,
  onPaymentSuccess: (result: SalePayment) => void
) => {
  const balance = useAppSelector(selectOutstandingBalance)
  const totals = useAppSelector(selectSaleTotals)
  const { handleIntegrationPayment } = useHandleIntegrationPayment(amount)
  const registerManager = useRegisterManager()
  const hasCashDiscounting = registerManager.hasCashDiscounting()
  const { systemPaymentTypes } = useFetchPaymentTypes()
  const { completeRoundCashPayment } = useCompleteCashPayment(
    onPaymentSuccess,
    systemPaymentTypes.roundingPaymentType
  )

  const handleCashDiscountingPayment = useCallback(
    (paymentType: ApiRetailerPaymentType): CashDiscountingPaymentOption => {
      return {
        paymentModalType: 'cashDiscounting',
        paymentType,
        amountTendered: amount,
      }
    },
    [amount]
  )

  const handleCashPayment = useCallback(
    (
      paymentType: ApiRetailerPaymentType
    ):
      | CashPaymentOption
      | CashDiscountingPaymentOption
      | IntegratedPaymentOption
      | undefined => {
      const amountTendered = vn(amount)
      const rounding =
        paymentType.config && paymentType.config.rounding
          ? paymentType.config.rounding
          : '0.01'
      const roundedAmountTendered = roundCash(amountTendered, paymentType)
      const roundedBalance = roundCash(balance, paymentType)

      if (paymentType.config && trim(paymentType.config.url)) {
        // Some people have cash, but with a URL for some reason. It behaves like an integrated payment for them
        return handleIntegrationPayment(paymentType)
      }

      if (hasCashDiscountingServiceFee(hasCashDiscounting, { totals })) {
        return handleCashDiscountingPayment(paymentType)
      }

      if (
        // Don't skip if it's not correctly rounded
        isCorrectlyRounded(amountTendered, vn(rounding)) &&
        // Skip if it's correctly rounded and 0 (0 dollar cash payment)
        (amountTendered.isZero() ||
          // Skip if it's correctly rounded, positive and greater than the balance
          (amountTendered.gt(0) && amountTendered.gt(balance)))
        // Don't ever skip for refunds as we always want to show the modal
      ) {
        completeRoundCashPayment(
          {
            remainder: roundedBalance.remainder.toString(),
            cashAmount: roundedAmountTendered.rounded.toString(),
          },
          paymentType
        )
      } else {
        return {
          paymentModalType: 'cash',
          paymentType,
          roundedAmountTendered: {
            rounded: vn(roundedAmountTendered.rounded.toString()), // The field in BigNumber which is something we don't have as direct dep in package.json, vendNumber and BigNumber are interchangable
            remainder: roundedAmountTendered.remainder,
          },
          roundedBalance: {
            rounded: vn(roundedBalance.rounded.toString()), // The field in BigNumber which is something we don't have as direct dep in package.json, vendNumber and BigNumber are interchangable
            remainder: roundedBalance.remainder,
          },
          rounding,
        }
      }
    },
    [
      amount,
      balance,
      completeRoundCashPayment,
      hasCashDiscounting,
      handleIntegrationPayment,
      handleCashDiscountingPayment,
      totals,
    ]
  )

  return {
    handleCashPayment,
  }
}
